import { Link, useLocation } from "wouter";
import {
  LayoutGrid,
  FileText,
  Users,
  ChevronDown,
  ChevronRight,
  Inbox,
  Briefcase,
  Tag,
  FileCheck2,
  UserPlus,
  Building,
  Shield,
  Truck,
  Wrench,
  BarChart3,
  Store,
  CalendarRange,
  GraduationCap,
  // CreditCard, // re-importar al reactivar el menú "Suscripción y Pagos"
  // TrendingUp, // re-importar al reactivar el grupo "CUMPLIMIENTO"
  Network,
  UserCheck,
  Boxes,
  LogOut,
  User,
  Bell,
} from "@/components/ds/icons";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Separator } from "@/components/ui/separator";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { EmpresaSwitcher } from "@/components/EmpresaSwitcher";
import { NovedadesBell } from "@/components/novedades/NovedadesBell";
import { useUser } from "@/contexts/UserContext";
import { NovedadesProvider, useNovedades } from "@/hooks/useNovedades";
import { SidebarBadgesProvider, useSidebarBadges } from "@/hooks/useSidebarBadges";
import { APP_VERSION } from "@/lib/version";

interface SubMenuItem {
  title: string;
  url: string;
  icon?: React.ComponentType<{ className?: string }>;
}

/**
 * El menú no lleva contadores. Los tuvo (documentos por subir, programas,
 * capacitaciones con plazo abierto, trabajadores atrasados y novedades sin
 * leer) y se sacaron: un número suelto al lado de una etiqueta no dice qué
 * cuenta ni qué se espera que uno haga con él, y la pregunta "¿qué significa
 * ese 3?" sólo se resuelve entrando. La cifra vive donde se puede leer con su
 * contexto —el Dashboard, la campana del header y cada pantalla—, no en la
 * navegación.
 */
interface MenuItem {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  url?: string;
  items?: SubMenuItem[];
  /** Ancla para el tour del dashboard: expone `data-tour` en el <li> del menú. */
  tourId?: string;
}

interface MenuGroup {
  label: string;
  items: MenuItem[];
}

const menuGroups: MenuGroup[] = [
  {
    label: "ACCESOS RÁPIDOS",
    items: [
      { title: "Dashboard", icon: LayoutGrid, url: "/" },
      { title: "Bandeja de Entrada", icon: Inbox, url: "/documentos/bandeja-entrada" },
      { title: "Trabajadores", icon: Users, url: "/personas/trabajadores", tourId: "menu-trabajadores" },
    ],
  },
  {
    label: "EMPRESA Y CONTRATOS",
    items: [
      { title: "Mi Empresa", icon: Store, url: "/configuracion/empresa", tourId: "menu-empresa" },
      // La URL sigue siendo /configuracion/capacitaciones a propósito: sólo
      // cambia el nombre visible. Cambiar la ruta rompería enlaces guardados,
      // el guide-registry y los accesos directos del onboarding.
      { title: "Documentos Empresa", icon: GraduationCap, url: "/configuracion/capacitaciones", tourId: "menu-capacitaciones" },
      { title: "Programa de Capacitaciones", icon: CalendarRange, url: "/capacitaciones/programa" },
      {
        title: "Gestión Comercial",
        icon: Briefcase,
        tourId: "menu-comercial",
        // Las dos partes de la relación primero; el vínculo (contrato) después.
        items: [
          { title: "Mandantes", url: "/comercial/mandantes", icon: Building },
          { title: "Contratistas", url: "/comercial/contratistas", icon: UserPlus },
          { title: "Contratos", url: "/comercial/contratos", icon: FileCheck2 },
        ],
      },
    ],
  },
  // Oculto por ahora (se reactivará más adelante). Las rutas /control/metas y
  // /control/cumplimiento siguen existiendo en App.tsx; solo se quita del menú.
  // Al reactivar: descomentar este grupo y re-importar Target y TrendingUp de @/components/ds/icons.
  // {
  //   label: "CUMPLIMIENTO",
  //   items: [
  //     { title: "Definir Metas", icon: Target, url: "/control/metas" },
  //     { title: "Tablero de Cumplimiento", icon: TrendingUp, url: "/control/cumplimiento" },
  //   ],
  // },
  {
    label: "CUENTA",
    items: [
      { title: "Novedades", icon: Bell, url: "/novedades" },
      { title: "Resumen de Cupos", icon: BarChart3, url: "/licencias/resumen-cupos" },
      { title: "Asignación de Usuarios", icon: UserCheck, url: "/licencias/asignacion-usuarios" },
    ],
  },
  {
    label: "CONFIGURACIÓN EMPRESA",
    items: [
      {
        title: "Mi organigrama",
        icon: Network,
        tourId: "menu-estructura",
        items: [
          { title: "Cargos", url: "/personas/cargos", icon: Briefcase },
          { title: "Áreas", url: "/personas/areas", icon: Tag },
        ],
      },
      {
        title: "Mis Equipos",
        icon: Boxes,
        tourId: "menu-recursos",
        items: [
          { title: "EPP", url: "/recursos/epp", icon: Shield },
          { title: "Vehículos", url: "/recursos/vehiculos", icon: Truck },
          { title: "Maquinaria", url: "/recursos/maquinaria", icon: Wrench },
        ],
      },
    ],
  },
];

export function AppLayout({ children }: { children: React.ReactNode }) {
  const [location, setLocation] = useLocation();
  const { user, logout } = useUser();
  // El menú ya no pinta contadores, pero estos conteos siguen vivos: el
  // Dashboard los lee del provider de más abajo y este es el único lugar que
  // los fetchea.
  const badges = useSidebarBadges();
  // Un solo fetch del feed para las dos superficies que lo muestran: campana
  // del header y pantalla de Novedades.
  const novedades = useNovedades();

  const handleLogout = async () => {
    await logout();
    setLocation("/login");
  };

  const userInitials = user?.name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase() || "U";

  const shell = (
    <SidebarProvider>
      <Sidebar>
        <SidebarHeader className="border-b border-sidebar-border px-4 py-3.5">
          <div className="flex items-center gap-2.5">
            <div className="flex size-[34px] shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground">
              <FileText className="size-[18px]" />
            </div>
            <div className="flex min-w-0 flex-col leading-tight">
              <span className="text-sm font-semibold tracking-[-0.01em] text-foreground">
                Fácil Control
              </span>
              <span className="text-[11px] text-soft">Sistema de Auditoría</span>
            </div>
          </div>
        </SidebarHeader>
        <SidebarContent className="gap-0">
          {/* Los grupos se separan sólo con aire — el diseño no lleva ninguna
              línea divisoria dentro del menú; la única regla horizontal es la
              del encabezado y la del pie. */}
          {menuGroups.map((group) => (
            <SidebarGroup key={group.label} className="px-2.5 py-0">
              <SidebarGroupLabel
                className={
                  "fc-group-label mb-1.5 h-auto px-2 " +
                  (group.label === "ACCESOS RÁPIDOS" ? "mt-2" : "mt-4")
                }
              >
                {group.label}
              </SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {group.items.map((item) => {
                    if (item.items) {
                      return (
                        <Collapsible
                          key={item.title}
                          defaultOpen={item.items.some((subItem) => location === subItem.url)}
                          className="group/collapsible"
                        >
                          <SidebarMenuItem data-tour={item.tourId}>
                            <CollapsibleTrigger asChild>
                              <SidebarMenuButton>
                                <item.icon className="size-[18px]" />
                                <span>{item.title}</span>
                                <ChevronDown className="ml-auto h-4 w-4 transition-transform group-data-[state=open]/collapsible:rotate-180" />
                              </SidebarMenuButton>
                            </CollapsibleTrigger>
                            <CollapsibleContent>
                              <SidebarMenuSub>
                                {item.items.map((subItem) => (
                                  <SidebarMenuSubItem key={subItem.url}>
                                    <SidebarMenuSubButton asChild isActive={location === subItem.url}>
                                      <Link href={subItem.url}>
                                        {subItem.icon && <subItem.icon className="h-4 w-4" />}
                                        <span>{subItem.title}</span>
                                      </Link>
                                    </SidebarMenuSubButton>
                                  </SidebarMenuSubItem>
                                ))}
                              </SidebarMenuSub>
                            </CollapsibleContent>
                          </SidebarMenuItem>
                        </Collapsible>
                      );
                    }

                    return (
                      <SidebarMenuItem key={item.title} data-tour={item.tourId}>
                        {/* Todos los ítems pesan igual: el único que se
                            destaca es el activo, y lo hace el primitivo. */}
                        <SidebarMenuButton asChild isActive={location === item.url}>
                          <Link href={item.url!}>
                            <item.icon className="size-[18px]" />
                            <span>{item.title}</span>
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    );
                  })}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          ))}
        </SidebarContent>
        <SidebarFooter className="border-t border-sidebar-border px-4 py-3">
          <span className="fc-num text-[11px] text-soft">{APP_VERSION}</span>
        </SidebarFooter>
      </Sidebar>
      <SidebarInset className="bg-canvas">
        <header className="flex h-14 shrink-0 items-center justify-between gap-2 border-b bg-background px-4">
          <div className="flex items-center gap-2">
            <SidebarTrigger />
            <Separator orientation="vertical" className="h-6" />
            <EmpresaSwitcher />
          </div>

          <div className="flex items-center gap-2">
            <NovedadesBell />

            {/* User Menu */}
            {user && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-1.5 transition-colors hover:bg-accent">
                  <Avatar className="size-7">
                    <AvatarFallback className="bg-primary text-[11.5px] font-semibold text-primary-foreground">
                      {userInitials}
                    </AvatarFallback>
                  </Avatar>
                  <div className="hidden flex-col items-start leading-tight sm:flex">
                    <span className="text-[13px] font-medium">{user.name}</span>
                    <span className="text-[11px] text-soft">{user.empresa?.nombre}</span>
                  </div>
                  <ChevronRight className="size-4 text-soft" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="flex flex-col gap-1">
                  <span>{user.name}</span>
                  <span className="text-xs font-normal text-muted-foreground">{user.email}</span>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuGroup>
                  <DropdownMenuItem asChild>
                    <Link href="/usuario/perfil" className="cursor-pointer">
                      <User className="mr-2 h-4 w-4" />
                      <span>Mi Perfil</span>
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuGroup>
                {/* Oculto por ahora (se reactivará más adelante). La ruta
                    /configuracion/suscripcion sigue existiendo en App.tsx; solo
                    se quita del menú. Al reactivar: descomentar este grupo y
                    re-importar CreditCard de @/components/ds/icons. */}
                {/* <DropdownMenuSeparator />
                <DropdownMenuGroup>
                  <DropdownMenuItem asChild>
                    <Link href="/configuracion/suscripcion" className="cursor-pointer">
                      <CreditCard className="mr-2 h-4 w-4" />
                      <span>Suscripción y Pagos</span>
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuGroup> */}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-destructive cursor-pointer">
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Cerrar Sesión</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            )}
          </div>
        </header>
        {/* Lienzo del DS: fondo #F2F4F6, padding 26/28 y ancho máximo 1360px. */}
        <main className="flex-1 overflow-auto px-4 py-5 md:px-7 md:py-[26px]">
          <div className="mx-auto w-full max-w-[1360px] animate-fc-in">
            <SidebarBadgesProvider value={badges}>{children}</SidebarBadgesProvider>
          </div>
        </main>
      </SidebarInset>
    </SidebarProvider>
  );

  // El provider envuelve TODO el shell y no sólo el `<main>`: la campana vive en
  // el header y el badge en el sidebar, los dos fuera del contenido.
  return <NovedadesProvider value={novedades}>{shell}</NovedadesProvider>;
}
